import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import * as Bixby from '$/core';
import { ClientService } from '$/services';
import { AppState } from '$/store';
import {
  getIsCurrentUserClientState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import { getIsCurrentUserAdminState } from '$/store/profile/selectors';
import './index.scss';
import DataSitesNavigation from '../DataSitesNavigation';

interface IRemovalsProps {
  isAdmin: boolean;
  isClient: boolean;
  isCreditSightsOpsSufOrRatings: boolean;
}

const DataSitesRemovals: React.FC<IRemovalsProps> = props => {
  const { isAdmin, isClient, isCreditSightsOpsSufOrRatings } = props;

  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  const [isLoading, setIsLoading] = useState(false);
  const [removalsError, setRemovalsError] = useState(undefined as string | undefined);
  const [removals, setRemovals] = useState(undefined as Bixby.Models.ClientCompanyRemoval[] | undefined);

  useEffect(() => {
    const getRemovalsAsync = async () => {
      setIsLoading(true);
      setRemovalsError(undefined);
      try {
        const removals: Bixby.Models.ClientCompanyRemoval[] = await ClientService.getCompanyRemovals();
        setRemovals(removals);
      } catch (e: any) {
        setRemovalsError('Error retrieving removals');
      } finally {
        setIsLoading(false);
      }
    };

    if (isClient === true && isCreditSightsOpsSufOrRatings !== true) {
      getRemovalsAsync();
    }
  }, [isClient, isCreditSightsOpsSufOrRatings]);

  let clientRemovalsViewHeader: string | null = null;
  if (removals && !Bixby.Utils.isNullOrEmpty(removals)) {
    const sitePhrasing: string = removals.length === 1 ? 'site' : 'sites';
    clientRemovalsViewHeader = `${removals.length} ${sitePhrasing} removed in the last 90 days`;
  }

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const content = isAdmin ? (
    <React.Fragment>
      <section>
        <div className="data-sites-removals-section">
          <h3>Admin User</h3>
        </div>
      </section>
    </React.Fragment>
  ) : isCreditSightsOpsSufOrRatings ? (
    <React.Fragment>
      <section>
        <div className="data-sites-removals-section">
          <h3>CreditSights User</h3>
        </div>
      </section>
    </React.Fragment>
  ) : isLoading && removals === undefined ? (
    <Loading pageLevel />
  ) : isClient ? (
    <React.Fragment>
      {!Bixby.Utils.isNullOrEmpty(removalsError) ? (
        <section>
          <div className="data-sites-removals-note">
            <p className="error">{removalsError}</p>
          </div>
        </section>
      ) : (
        <section>
          <div className="data-sites-removals-note">
            <p>{clientRemovalsViewHeader}</p>
          </div>
          <div className="data-sites-removals-section">
            {Bixby.Utils.isNullOrEmpty(removals) && <NoDataFound dataType="removals" />}

            {!Bixby.Utils.isNullOrEmpty(removals) && (
              <div className="data-sites-removals-data-grid">
                <div className="grid_container">
                  <div className="grid ag-theme-alpine">
                    <AgGridReact
                      enableRangeSelection={false}
                      getContextMenuItems={getContextMenuItems}
                      defaultColDef={{
                        flex: 1,
                        minWidth: 100,
                        sortable: true,
                        resizable: true,
                        lockPinned: true,
                      }}
                      onGridReady={onGridReady}
                      rowData={removals}
                      suppressDragLeaveHidesColumns={true}
                      suppressMovableColumns={true}
                      suppressCellFocus={true}
                      animateRows={true}
                      rowHeight={32}
                      headerHeight={32}
                      columnDefs={[
                        {
                          field: 'dataSiteHost',
                          headerName: 'Bank',
                          minWidth: 250,
                          pinned: 'left',
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          cellClass: 'ag-cell-left',
                        },
                        {
                          field: 'dataSiteName',
                          headerName: 'Data Site Name',
                          minWidth: 650,
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          cellClass: 'ag-cell-left',
                        },
                        {
                          field: 'deletedDate',
                          headerName: 'Removal Date',
                          width: 100,
                          valueGetter: params => {
                            if (params.data?.['deletedDate']) {
                              return new Date(params.data['deletedDate']).toLocaleDateString() as any;
                            } else {
                              return '';
                            }
                          },
                        },
                      ]}></AgGridReact>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </React.Fragment>
  ) : null;

  return <DataSitesNavigation>{content}</DataSitesNavigation>;
};

const mapStateToProps = (state: AppState) => {
  const { isClient } = getIsCurrentUserClientState(state);
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);

  return {
    isAdmin,
    isClient,
    isCreditSightsOpsSufOrRatings: isCreditSightsAnalyst || isCreditSightsOps || isSuFAnalyst || isRatingsAnalyst,
  };
};

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(DataSitesRemovals);
